import { apiRequest } from './apiClient'
import { getValidToken } from './authService'

// Helper to add auth header
function authHeaders() {
  const token = getValidToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// ============================================================================
// System Settings
// ============================================================================

export async function fetchBillingSettings() {
  return apiRequest('/settings/billing', {
    method: 'GET',
    headers: authHeaders(),
  })
}

export async function updateBillingSettings(data) {
  return apiRequest('/settings/billing/update', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
}

// ============================================================================
// Dealers
// ============================================================================

export async function fetchDealers(search = '') {
  const query = search ? `?search=${encodeURIComponent(search)}` : ''
  return apiRequest(`/dealers${query}`, {
    method: 'GET',
    headers: authHeaders(),
  })
}

export async function fetchDealer(id) {
  return apiRequest(`/dealers/${id}`, {
    method: 'GET',
    headers: authHeaders(),
  })
}

export async function createDealer(data) {
  return apiRequest('/dealers/create', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
}

export async function updateDealer(id, data) {
  return apiRequest(`/dealers/${id}/update`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
}

export async function deleteDealer(id) {
  return apiRequest(`/dealers/${id}/delete`, {
    method: 'POST',
    headers: authHeaders(),
  })
}

export async function fetchDealerBalance(id, limit = 50, offset = 0) {
  return apiRequest(`/dealers/${id}/balance?limit=${limit}&offset=${offset}`, {
    method: 'GET',
    headers: authHeaders(),
  })
}

export async function adjustDealerBalance(id, amount, description) {
  return apiRequest(`/dealers/${id}/balance/adjust`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ amount, description }),
  })
}

// ============================================================================
// Dealer Profile (for DEALER_ADMIN)
// ============================================================================

export async function fetchDealerProfile() {
  return apiRequest('/dealer/profile', {
    method: 'GET',
    headers: authHeaders(),
  })
}

export async function updateDealerProfile(data) {
  return apiRequest('/dealer/profile/update', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
}

export async function fetchOwnBalance(limit = 50, offset = 0) {
  return apiRequest(`/dealer/balance?limit=${limit}&offset=${offset}`, {
    method: 'GET',
    headers: authHeaders(),
  })
}

// ============================================================================
// Client Pricing
// ============================================================================

export async function fetchClientPricing(clientId) {
  return apiRequest(`/clients/${clientId}/pricing`, {
    method: 'GET',
    headers: authHeaders(),
  })
}

export async function setClientPricing(clientId, data) {
  return apiRequest(`/clients/${clientId}/pricing/set`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
}

export async function fetchClientPricingHistory(clientId) {
  return apiRequest(`/clients/${clientId}/pricing/history`, {
    method: 'GET',
    headers: authHeaders(),
  })
}

export async function fetchClientsWithPricing() {
  return apiRequest('/clients/pricing', {
    method: 'GET',
    headers: authHeaders(),
  })
}

// ============================================================================
// Invoices
// ============================================================================

export async function fetchInvoices(filters = {}) {
  const params = new URLSearchParams()
  if (filters.type) params.append('type', filters.type)
  if (filters.status) params.append('status', filters.status)
  if (filters.periodStart) params.append('periodStart', filters.periodStart)
  if (filters.periodEnd) params.append('periodEnd', filters.periodEnd)
  if (filters.limit) params.append('limit', filters.limit)
  if (filters.offset) params.append('offset', filters.offset)

  const query = params.toString() ? `?${params.toString()}` : ''
  return apiRequest(`/invoices${query}`, {
    method: 'GET',
    headers: authHeaders(),
  })
}

export async function fetchInvoice(id) {
  return apiRequest(`/invoices/${id}`, {
    method: 'GET',
    headers: authHeaders(),
  })
}

export async function createInvoice(data) {
  return apiRequest('/invoices/create', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
}

export async function sendInvoice(id) {
  return apiRequest(`/invoices/${id}/send`, {
    method: 'POST',
    headers: authHeaders(),
  })
}

export async function cancelInvoice(id) {
  return apiRequest(`/invoices/${id}/cancel`, {
    method: 'POST',
    headers: authHeaders(),
  })
}

/** Оплата рахунку: реквізити, призначення з особовим рахунком, посилання НБУ і QR */
export async function fetchInvoicePayment(id) {
  return apiRequest(`/invoices/${id}/payment`, {
    method: 'GET',
    headers: authHeaders(),
  })
}

export async function fetchMyInvoices(limit = 50, offset = 0) {
  return apiRequest(`/my-invoices?limit=${limit}&offset=${offset}`, {
    method: 'GET',
    headers: authHeaders(),
  })
}

// ============================================================================
// Личный счёт (баланс) — показывается, если дилер включил видимость
// ============================================================================

export async function fetchMyBalance() {
  return apiRequest('/my-balance', {
    method: 'GET',
    headers: authHeaders(),
  })
}

// payerId: 'main' — реквизиты аккаунта, число — доп. плательщик, null — вся история
export async function fetchMyBalanceTransactions(limit = 10, offset = 0, payerId = null) {
  const payerQuery = payerId !== null && payerId !== undefined ? `&payerId=${encodeURIComponent(payerId)}` : ''
  return apiRequest(`/my-balance/transactions?limit=${limit}&offset=${offset}${payerQuery}`, {
    method: 'GET',
    headers: authHeaders(),
  })
}

// ============================================================================
// Payments
// ============================================================================

export async function fetchPayments(filters = {}) {
  const params = new URLSearchParams()
  if (filters.type) params.append('type', filters.type)
  if (filters.status) params.append('status', filters.status)
  if (filters.dateFrom) params.append('dateFrom', filters.dateFrom)
  if (filters.dateTo) params.append('dateTo', filters.dateTo)
  if (filters.limit) params.append('limit', filters.limit)
  if (filters.offset) params.append('offset', filters.offset)

  const query = params.toString() ? `?${params.toString()}` : ''
  return apiRequest(`/payments${query}`, {
    method: 'GET',
    headers: authHeaders(),
  })
}

export async function createPayment(data) {
  return apiRequest('/payments/create', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
}

export async function confirmPayment(id) {
  return apiRequest(`/payments/${id}/confirm`, {
    method: 'POST',
    headers: authHeaders(),
  })
}

export async function refundPayment(id) {
  return apiRequest(`/payments/${id}/refund`, {
    method: 'POST',
    headers: authHeaders(),
  })
}

export async function fetchMyPayments(limit = 50, offset = 0) {
  return apiRequest(`/my-payments?limit=${limit}&offset=${offset}`, {
    method: 'GET',
    headers: authHeaders(),
  })
}

// ============================================================================
// Notifications (Client Portal - uses /my-notifications for DEALER_USER)
// ============================================================================

// filters: { types: string[], q: string } — фильтр ленты колокольчика по типу события и поиск
export async function fetchNotifications(limit = 50, offset = 0, unreadOnly = false, category = null, filters = {}) {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset), unreadOnly: String(unreadOnly) })
  if (category) params.set('category', category)
  if (filters.types?.length) params.set('types', filters.types.join(','))
  if (filters.q) params.set('q', filters.q)
  return apiRequest(`/my-notifications?${params}`, {
    method: 'GET',
    headers: authHeaders(),
  })
}

export async function fetchUnreadCount() {
  return apiRequest('/notifications/unread-count', {
    method: 'GET',
    headers: authHeaders(),
  })
}

export async function fetchBanner() {
  return apiRequest('/notifications/banner', {
    method: 'GET',
    headers: authHeaders(),
  })
}

export async function markNotificationRead(id) {
  return apiRequest(`/notifications/${id}/read`, {
    method: 'POST',
    headers: authHeaders(),
  })
}

// filters — тот же фильтр, что у ленты: при активном фильтре сервер отмечает только отфильтрованное
export async function markAllNotificationsRead(category = null, filters = {}) {
  const body = {}
  if (category) body.category = category
  if (filters.types?.length) body.types = filters.types
  if (filters.q) body.q = filters.q
  return apiRequest('/notifications/read-all', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
  })
}

export async function dismissBanner() {
  return apiRequest('/notifications/banner/dismiss', {
    method: 'POST',
    headers: authHeaders(),
  })
}

// ============================================================================
// Client Management (warn/block/unblock)
// ============================================================================

export async function notifyClient(clientId, data) {
  return apiRequest(`/clients/${clientId}/notify`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
}

export async function warnClient(clientId, message, invoiceId = null) {
  return apiRequest(`/clients/${clientId}/warn`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ message, invoiceId }),
  })
}

export async function blockClient(clientId, reason) {
  return apiRequest(`/clients/${clientId}/block`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ reason }),
  })
}

export async function unblockClient(clientId) {
  return apiRequest(`/clients/${clientId}/unblock`, {
    method: 'POST',
    headers: authHeaders(),
  })
}

export async function suspendClient(clientId) {
  return apiRequest(`/clients/${clientId}/suspend`, {
    method: 'POST',
    headers: authHeaders(),
  })
}

// Акт звірки взаєморозрахунків (портал): организации для выбора стороны + файл PDF/XLSX
export async function fetchMyReconciliationOptions() {
  return apiRequest('/my-reconciliation/options', {
    method: 'GET',
    headers: authHeaders(),
  })
}

export async function fetchMyReconciliationFile({ from, to, orgId = '', payerId = '' }, format = 'pdf', inline = false) {
  const token = getValidToken()
  const apiBase = import.meta.env.VITE_CLIENT_API_BASE || '/client/api'
  // payerId: '' = реквизиты аккаунта, id = одно из юрлиц клиента
  const q = new URLSearchParams({ from, to, orgId: orgId === null ? '' : String(orgId), payerId: payerId === null ? '' : String(payerId), format })
  if (inline) q.append('inline', '1')
  const response = await fetch(`${apiBase}/my-reconciliation?${q.toString()}`, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.message || 'download_failed')
  }
  return response.blob()
}

// Договоры и акты клиента (портал: только просмотр и PDF)
export async function fetchMyContracts() {
  return apiRequest('/my-contracts', {
    method: 'GET',
    headers: authHeaders(),
  })
}

/** kind: contracts | acts; signed — скан подписанного вместо сгенерированного PDF */
export async function fetchMyDocumentPdf(kind, id, { inline = false, signed = false } = {}) {
  const token = getValidToken()
  const apiBase = import.meta.env.VITE_CLIENT_API_BASE || '/client/api'
  const query = [inline ? 'inline=1' : '', signed ? 'signed=1' : ''].filter(Boolean).join('&')
  const response = await fetch(`${apiBase}/my-${kind}/${id}/pdf${query ? `?${query}` : ''}`, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.message || 'pdf_generation_failed')
  }
  return response.blob()
}
